import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import NoImage from "../../../images/no-image.jpg"; // replace with your placeholder image
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCountries } from "../../../services/CountryService";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { Button } from "react-bootstrap";
import {
  addSuccessStory,
  getSuccessStoryById,
  updateSuccessStory,
} from "../../../services/SuccessStoryService";

const AddSuccessStory = () => {
  const [formData, setFormData] = useState({
    user_name: "",
    user_message: "",
    user_desc: "",
    country_id: null,
  });

  const [userImage, setUserImage] = useState(null);
  const [existingUserImage, setExistingUserImage] = useState("");
  const [storyImage, setStoryImage] = useState(null);
  const [existingStoryImage, setExistingStoryImage] = useState("");
  const [errors, setErrors] = useState({});
  const hasFetched = useRef(false);
  const hasDataFetched = useRef(false);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // dropdown mock data
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryPage, setCountryPage] = useState(1);
  const [countryTotalPages, setCountryTotalPages] = useState(1);
  const [countryLoading, setCountryLoading] = useState(false);

  const fetchCountry = async (pageNumber = 1, query = "") => {
    if (pageNumber > countryTotalPages) return; // no more pages
    try {
      setCountryLoading(true);
      const limit = 5; // set per-page limit
      const response = await getCountries(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: c.name }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCountryOptions(options);
      } else {
        // append for scroll
        setCountryOptions((prev) => [...prev, ...options]);
      }

      setCountryPage(pagination?.page || pageNumber);
      setCountryTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleUserFileChange = (e) => {
    setUserImage(e.target.files[0]);
  };

  const handleStoryFileChange = (e) => {
    setStoryImage(e.target.files[0]);
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      country_ids: selectedOption,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!id && !userImage) newErrors.user_image = "User image is required";
    if (!id && !storyImage) newErrors.story_image = "Story image is required";
    if (!formData.user_name.trim())
      newErrors.user_name = "User name is required";
    if (!formData.user_message.trim())
      newErrors.user_message = "User message is required";
    if (!formData.user_desc.trim())
      newErrors.user_desc = "User description is required";
    if (!formData.country_id) newErrors.country_id = "Country is required";

    return newErrors;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [countryRes] = await Promise.all([getCountries(1, 5000)]);

        const countryOpts = (countryRes.data?.data || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCountryOptions(countryOpts);

        // 2️⃣ Then fetch blog by ID
        if (id) {
          const res = await getSuccessStoryById(id);
          if (res.data?.success) {
            const successStory = res.data.data;

            setFormData({
              user_name: successStory.user_name || "",
              user_message: successStory.user_message || "",
              user_desc: successStory.user_desc || "",
              country_id:
                countryOpts.find((c) => c.value === successStory.country_id) ||
                null,
            });
            setExistingUserImage(successStory.user_image || "");
            setExistingStoryImage(successStory.story_image || "");
          } else {
            console.error("Failed to fetch success story by ID");
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log("Validation Errors:", validationErrors);
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      if (userImage) fd.append("user_image", userImage);
      if (storyImage) fd.append("story_image", storyImage);

      // append normal fields
      const submitData = {
        ...formData,
      };

      // append normal fields
      Object.keys(submitData).forEach((key) => {
        if (key === "country_id") {
          fd.append(`${key}`, submitData[key]?.value || "");
        } else {
          fd.append(key, submitData[key]);
        }
      });

      if (id) {
        const response = await updateSuccessStory(id, fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.data?.message ||
              "Success story details updated successfully!",
          });
          navigate("/success-story", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        } else {
          console.error(
            "Error updating success story:",
            response?.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Failed to update success story",
          });
        }
      } else {
        const response = await addSuccessStory(fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.data?.message ||
              "Success story details added successfully",
          });
          navigate("/success-story", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        } else {
          console.error(
            "Error updating success story:",
            response.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data?.message || "Failed to added success story",
          });
        }
      }
    } catch (err) {
      console.error(
        "Error while adding or updating success story:",
        err || "Unknown error"
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err || "Failed to adding or updating success story",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Success Story Details" : "Add Success Story Details"
          }`}
        motherMenu="Success Story"
        activeMenuLink={`${id ? `edit-success-story/${id}` : "add-success-story"
          }`}
        motherMenuLink={"success-story"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    {/* User Image */}
                    <div
                      onClick={() => {
                        navigate("/success-story", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Success Story List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">User Image</label>
                      <div>
                        <img
                          src={
                            userImage
                              ? URL.createObjectURL(userImage)
                              : existingUserImage || NoImage
                          }
                          alt="Blog Image"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleUserFileChange}
                          id="imageUpload"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                      {errors.user_image && (
                        <div
                          className="text-danger mt-1"
                          id="user_image_error"
                        >
                          {errors.user_image}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Story Image</label>
                      <div>
                        <img
                          src={
                            storyImage
                              ? URL.createObjectURL(storyImage)
                              : existingStoryImage || NoImage
                          }
                          alt="Story Image"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleStoryFileChange}
                          id="imageUpload2"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload2"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                      {errors.story_image && (
                        <div
                          className="text-danger mt-1"
                          id="story_image_error"
                        >
                          {errors.story_image}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="user_name">
                        User Name
                      </label>
                      <input
                        id="user_name"
                        type="text"
                        name="user_name"
                        className={`form-control ${errors.user_name ? "is-invalid" : ""
                          }`}
                        value={formData.user_name}
                        onChange={handleChange}
                        placeholder="Enter user name"
                      />
                      {errors.user_name && (
                        <div className="invalid-feedback" id="user_name_error">
                          {errors.user_name}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="user_message">
                        User Message
                      </label>
                      <textarea
                        id="user_message"
                        name="user_message"
                        className={`form-control ${errors.user_message ? "is-invalid" : ""
                          }`}
                        rows={2}
                        value={formData.user_message}
                        onChange={handleChange}
                        placeholder="Enter user message"
                      ></textarea>
                      {errors.user_message && (
                        <div
                          className="invalid-feedback"
                          id="user_message_error"
                        >
                          {errors.user_message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="user_desc">
                        User Description
                      </label>
                      <textarea
                        id="user_desc"
                        name="user_desc"
                        className={`form-control ${errors.user_desc ? "is-invalid" : ""
                          }`}
                        rows={2}
                        value={formData.user_desc}
                        onChange={handleChange}
                        placeholder="Enter user description"
                      ></textarea>
                      {errors.user_desc && (
                        <div className="invalid-feedback" id="user_desc_error">
                          {errors.user_desc}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="country_id">
                        Country
                      </label>
                      <Select
                        id="country_id"
                        value={formData.country_id}
                        onChange={(value) =>
                          handleSelectChange("country_id", value)
                        }
                        options={countryOptions}
                        placeholder="Select country"
                        closeMenuOnSelect={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        onMenuScrollToBottom={() =>
                          fetchCountry(countryPage + 1)
                        } // load next page
                        // onInputChange={(inputValue) => fetchCountry(1, inputValue)} // search resets page
                        isLoading={countryLoading}
                      />
                      {errors.country_id && (
                        <div className="text-danger mt-1" id="country_id_error">
                          {errors.country_id}
                        </div>
                      )}
                    </div>

                    <hr style={{ margin: "0px" }} className="mx-2" />
                    <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                      <Button
                        id="back_button"
                        type="button"
                        variant="secondary"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          navigate("/success-story", {
                            state: {
                              page: location.state?.page || 1,
                              searchTerm: location.state?.searchTerm || "",
                            },
                          })
                        }}
                      >
                        Back
                      </Button>

                      {/* Save / Update Button */}
                      <Button
                        id="save_button"
                        type="submit"
                        className="btn btn-primary btn-sm"
                      >
                        {id ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddSuccessStory;
